// Hand-written types mirroring the Rust DTOs. The OpenAPI spec at
// /api/openapi.json is the source of truth — when these drift, regenerate
// via `npx openapi-typescript /api/openapi.json` (future work).

export type ProtocolKind = "torrent" | "usenet";

/// How the indexer's API key is delivered on the wire (#43). Wire
/// values match the column stored in `indexers.auth_method` and
/// `arr_indexers::AuthMethod::as_wire`.
export type IndexerAuthMethod =
  | "query-apikey"
  | "header-x-api-key"
  | "header-bearer"
  | "header-basic"
  | "none";

export interface IndexerRow {
  id: number;
  name: string;
  base_url: string;
  enabled: boolean;
  priority: number;
  categories: string;
  protocol: ProtocolKind;
  last_health_check_at: string | null;
  healthy: boolean | null;
  /// Per-indexer User-Agent override (#44). null = fall back to the
  /// global default (`indexer.default_user_agent`, itself defaulting
  /// to `arr-in-one/{VERSION}`).
  user_agent: string | null;
  /// How the API key is delivered to this indexer (#43). Existing rows
  /// land on `query-apikey`, the legacy behaviour.
  auth_method: IndexerAuthMethod;
  created_at: string;
  updated_at: string;
}

export interface CreateIndexerRequest {
  name: string;
  base_url: string;
  api_key?: string | null;
  protocol: ProtocolKind;
  enabled?: boolean;
  priority?: number;
  categories?: number[];
}

export interface UpdateIndexerRequest {
  name?: string;
  base_url?: string;
  api_key?: string | null;
  enabled?: boolean;
  priority?: number;
  categories?: number[];
  protocol?: ProtocolKind;
  /// Per-indexer User-Agent override (#44). Empty string clears the
  /// override (falls back to the global default). Omit to keep the
  /// existing value.
  user_agent?: string;
  /// Per-indexer auth method (#43). Validated server-side against the
  /// closed set; unknown values yield HTTP 400. Omit to keep the
  /// existing value.
  auth_method?: IndexerAuthMethod;
}

export interface TestIndexerResponse {
  healthy: boolean;
  message: string | null;
  checked_at: string;
}

/// One row of the curated indexer preset library (#34) — what the
/// "Add indexer" modal renders as a one-click card. After picking a
/// preset the create form pre-fills `base_url` / `protocol` /
/// `categories` and only the API key still needs to be entered.
export interface IndexerPreset {
  id: string;
  name: string;
  base_url: string;
  protocol: ProtocolKind;
  default_categories: number[];
  website: string;
  description: string;
  requires_api_key: boolean;
}

/// One row of the per-indexer search audit log (#41).
/// `finished_at` is null while the call is in flight (or if the
/// process crashed mid-search). `status_code` is null on transport /
/// parse errors that didn't get an HTTP response.
export interface IndexerSearchRow {
  id: number;
  indexer_id: number;
  query: string;
  started_at: string;
  finished_at: string | null;
  status_code: number | null;
  releases_returned: number | null;
  error: string | null;
}

/// 24h aggregate stats over the audit log.
export interface IndexerStats {
  total_queries: number;
  /// 0.0–1.0 — fraction of queries that finished without error.
  success_rate: number;
  avg_response_ms: number;
  zero_result_count: number;
}

/// One per-indexer query rewrite rule (#38). Patterns are validated as
/// Rust regexes server-side; bad regexes are rejected at insert time
/// with HTTP 400.
export interface IndexerQueryRewriteRow {
  id: number;
  indexer_id: number;
  pattern: string;
  replacement: string;
  ord: number;
  created_at: string;
  updated_at: string;
}

export interface CreateRewriteRequest {
  pattern: string;
  replacement: string;
  ord?: number;
}

export interface UpdateRewriteRequest {
  pattern?: string;
  replacement?: string;
  ord?: number;
}

/// One per-indexer download-URL rewrite rule (#42). Same shape as a
/// query-rewrite rule but applied to each release's `download_url`
/// just before the download client / proxy fetch.
export interface IndexerDownloadRewriteRow {
  id: number;
  indexer_id: number;
  pattern: string;
  replacement: string;
  ord: number;
  created_at: string;
  updated_at: string;
}

export interface CreateDownloadRewriteRequest {
  pattern: string;
  replacement: string;
  ord?: number;
}

export interface UpdateDownloadRewriteRequest {
  pattern?: string;
  replacement?: string;
  ord?: number;
}

/// Per-indexer + per-media tag (#39).
///
/// `color` is a free-form CSS color string; null falls through to the
/// default chip treatment. The backend caps name length at 64 chars
/// and color length at 32 chars.
export interface TagRow {
  id: number;
  name: string;
  color: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateTagRequest {
  name: string;
  color?: string | null;
}

export interface UpdateTagRequest {
  name?: string;
  /**
   * Send `null` to explicitly clear the color back to NULL; omit the
   * key to leave it unchanged. The backend distinguishes "missing"
   * from "explicitly null".
   */
  color?: string | null;
}

export interface SetTagsRequest {
  tag_ids: number[];
}

/**
 * Media-kind discriminator for the per-media tag endpoints. Mirrors
 * the Rust `MediaTagKind` enum and the `media_tags.media_kind` CHECK
 * constraint in migration 0051. Kept as a type-only re-export so it
 * stays in sync with the canonical wire-format.
 */
export type MediaTagKind = "movie" | "episode" | "album" | "series" | "artist";

export type ClientKind = "qbittorrent" | "sabnzbd" | "nzbget" | "embedded";

/// Subset of `ClientKind` that's user-creatable in the Add modal.
/// `embedded` is auto-seeded on first migration and can't be added by
/// hand, so the dropdown excludes it.
export type CreatableClientKind = Exclude<ClientKind, "embedded">;

export interface DownloadClientRow {
  id: number;
  name: string;
  kind: ClientKind;
  protocol: ProtocolKind;
  enabled: boolean;
  priority: number;
  host: string;
  port: number;
  use_ssl: boolean;
  username: string | null;
  category: string | null;
  healthy: boolean | null;
  last_health_check_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateDownloadClient {
  name: string;
  kind: ClientKind;
  protocol: ProtocolKind;
  enabled?: boolean;
  priority?: number;
  host: string;
  port: number;
  use_ssl?: boolean;
  username?: string | null;
  password?: string | null;
  api_key?: string | null;
  category?: string | null;
}

export interface UpdateDownloadClient {
  name?: string;
  enabled?: boolean;
  priority?: number;
  host?: string;
  port?: number;
  use_ssl?: boolean;
  username?: string | null;
  password?: string | null;
  api_key?: string | null;
  category?: string | null;
}

export interface TestResult {
  ok: boolean;
  message: string | null;
}

/// Per-server breakdown returned by the embedded NZB downloader's Test
/// endpoint. The frontend renders one row per server in a modal.
export interface EmbeddedServerProbe {
  server_id: string;
  server_name: string;
  host: string;
  ok: boolean;
  message: string | null;
}

export interface EmbeddedTestResult {
  ok: boolean;
  message: string | null;
  servers: EmbeddedServerProbe[];
}

export interface DownloadStatus {
  client_id: number;
  external_id: string;
  name: string;
  progress: number;
  state: "queued" | "downloading" | "paused" | "completed" | "failed" | "unknown";
  size: number;
  downloaded: number;
  eta_seconds: number | null;
}

export type MinimumAvailability =
  | "announced"
  | "in-cinemas"
  | "released"
  | "physical";

/**
 * Canonical edition slugs the backend recognises. Mirrors
 * `arr_core::quality::MOVIE_EDITIONS`. NULL on the row means "no
 * preference — selector ignores edition entirely". Issue #47.
 */
export type MovieEdition =
  | "theatrical"
  | "directors-cut"
  | "extended"
  | "ultimate"
  | "final-cut"
  | "remastered"
  | "redux"
  | "unrated"
  | "imax"
  | "uncut";

export interface Movie {
  id: number;
  tmdb_id: number | null;
  imdb_id: string | null;
  title: string;
  sort_title: string;
  year: number | null;
  overview: string | null;
  poster_url: string | null;
  runtime_minutes: number | null;
  monitored: boolean;
  has_file: boolean;
  path: string | null;
  added_by: number | null;
  created_at: string;
  updated_at: string;
  /** ISO-8601 date of theatrical release in the user's TMDB region. */
  theatrical_release: string | null;
  /** ISO-8601 date of digital (streaming/VOD) release. */
  digital_release: string | null;
  /** ISO-8601 date of physical (disc) release. */
  physical_release: string | null;
  /** Per-movie override of `movies.minimum_availability` global. NULL means
   *  "use global default". */
  minimum_availability: MinimumAvailability | null;
  /** Canonical YouTube trailer URL pulled from TMDB's `/movie/{id}/videos`.
   *  NULL when TMDB has no matching entry, or the upstream call failed
   *  during the last add/refresh. Issue #50. */
  trailer_url: string | null;
  /** TMDB genre names, e.g. `["Action","Drama"]`. Stored on the row as
   *  a JSON-encoded TEXT column; the API decodes it before serialising
   *  so we get a real array on the wire. NULL when the lookup hasn't
   *  populated it yet (or the movie has no genres). Issue #52. */
  genres: string[] | null;
  /** Region-specific certification (e.g. `"PG-13"`, `"R"`, `"12A"`),
   *  picked from `/movie/{id}/release_dates` for the configured TMDB
   *  region. NULL when the region has no event with a non-empty cert.
   *  Issue #52. */
  certification: string | null;
  /** TMDB collection id this movie belongs to (Lord of the Rings, MCU,
   *  etc). NULL for stand-alone movies. Issue #49. */
  collection_tmdb_id: number | null;
  /** Per-movie edition preference. NULL means "no preference — pick
   *  whatever release wins on rank + score". Otherwise one of the
   *  canonical {@link MovieEdition} slugs. The grab selector boosts
   *  matching releases and drops mismatches when at least one
   *  matching candidate survives. Issue #47. */
  preferred_edition: MovieEdition | null;
  /** Migration 0062: the household-isolation flag. `true` routes
   *  imports to `movies.private_library_path` (instead of the public
   *  root the household media server is mounted at) AND hides the row
   *  from the default Library list. Toggling the flag is cheap — it
   *  doesn't relocate an already-filed file. */
  private: boolean;
}

/** Row of `/api/v1/collections`. Includes a coverage count. Issue #49. */
export interface MovieCollection {
  tmdb_id: number;
  name: string;
  overview: string | null;
  poster_url: string | null;
  monitored: boolean;
  last_refreshed_at: string | null;
  created_at: string;
}

export interface CollectionListEntry extends MovieCollection {
  /** Number of parts already in the library. */
  in_library: number;
  /** Total parts the collection contains. Falls back to `in_library`
   *  when the collection has never been refreshed. */
  total: number;
}

export interface CollectionPart {
  tmdb_id: number;
  title: string;
  year: number | null;
  poster_url: string | null;
  in_library: boolean;
  movie_id: number | null;
}

export interface CollectionDetail {
  collection: MovieCollection;
  parts: CollectionPart[];
}

export interface AddAllResponse {
  added: number;
  skipped: number;
}

export interface AddMovieRequest {
  tmdb_id: number;
  monitored?: boolean;
  /** Migration 0062: pre-flag the new movie as private. Default false. */
  private?: boolean;
}

export interface UpdateMovieRequest {
  monitored?: boolean;
  path?: string | null;
  /** Send a value to override, "default" or empty string to clear, omit
   *  to leave unchanged. */
  minimum_availability?: MinimumAvailability | "default" | "" | null;
  /** Per-movie edition preference (issue #47). Send a {@link
   *  MovieEdition} slug to set, "any" or empty string to clear, omit
   *  to leave unchanged. */
  preferred_edition?: MovieEdition | "any" | "" | null;
  /** Migration 0062: toggle the private flag. */
  private?: boolean;
}

export interface MovieLookupResult {
  external_ids: { tmdb?: number; imdb?: string };
  title: string;
  year: number | null;
  overview: string | null;
  poster_url: string | null;
  kind: "movie" | "tv" | "music";
}

export interface Artist {
  id: number;
  mbid: string;
  name: string;
  sort_name: string;
  overview: string | null;
  poster_url: string | null;
  /**
   * Wide 16:9 backdrop sourced from fanart.tv (issue #12). The
   * artist detail page renders this as a hero with the title
   * overlaid; null falls through to the existing solid header.
   */
  fanart_url: string | null;
  /** Long-strip header treatment from fanart.tv. */
  banner_url: string | null;
  /** Transparent clearlogo / title treatment from fanart.tv. */
  logo_url: string | null;
  monitored: boolean;
  path: string | null;
  added_by: number | null;
  created_at: string;
  updated_at: string;
  /** ISO-8601 UTC stamp of the last successful MusicBrainz
   *  refresh (issue #6). NULL = never refreshed since the column
   *  was added; the background loop walks NULL rows first. */
  last_refreshed_at: string | null;
}

export interface Album {
  id: number;
  mbid: string;
  artist_id: number;
  title: string;
  release_date: string | null;
  /** "Album" | "EP" | "Single" — free-form from MusicBrainz, normalised
   *  to those three at import time via the release filter. */
  primary_type: string | null;
  /** JSON-encoded MusicBrainz secondary types: `["Live"]`,
   *  `["Live","Compilation"]`, etc. NULL = canonical release. */
  secondary_types: string | null;
  /** MusicBrainz disambiguation for the release-group: "remastered",
   *  "deluxe edition", "live in Tokyo", etc. NULL when MB returns
   *  empty/missing. Surfaced under the title on detail and in
   *  parens after the title in album lists to distinguish
   *  same-titled albums. */
  disambiguation: string | null;
  poster_url: string | null;
  /**
   * Round disc-art image from fanart.tv (issue #12). NULL when
   * fanart.tv has no `cdart` for this release-group. Surfaced
   * beside the cover on the album detail page.
   */
  disc_art_url: string | null;
  monitored: boolean;
  has_file: boolean;
  path: string | null;
  created_at: string;
  updated_at: string;
  /**
   * Issue #7: per-album preferred MusicBrainz *release* MBID
   * (Standard / Deluxe / Japanese / 2014 remaster, etc). NULL means
   * "no preference" — selector treats the album as release-group
   * keyed. Validated against `album_releases.mbid` on PATCH.
   * Informational today; the selector integration is documented
   * future work.
   */
  preferred_release_mbid: string | null;
  /**
   * Issue #10: ISO-8601 UTC timestamp the music RSS-sync loop last
   * considered this album. Stamped on every visit (success or
   * failure) so a failed grab backs off for a full
   * `music.rss_interval_minutes` rather than retrying every tick.
   * NULL = "never RSS-checked yet". Surfaced on the album detail
   * page as a muted "RSS-checked Xh ago" footer.
   */
  last_rss_sync_at: string | null;
}

/**
 * One candidate release within an album's MusicBrainz release-group
 * (issue #7). Backs the per-album "Releases" picker. Persisted into
 * `album_releases`; lazy-loaded from MB on the first
 * `GET /api/v1/albums/:id/releases` call.
 */
export interface AlbumRelease {
  id: number;
  album_id: number;
  /** MusicBrainz release MBID (NOT release-group). */
  mbid: string;
  title: string | null;
  /** ISO 3166-1 alpha-2 ("US", "GB"). */
  country: string | null;
  /** ISO-8601 date for *this* release. */
  date: string | null;
  /** "CD" / "Digital Media" / "Vinyl" — first medium format. */
  format: string | null;
  track_count: number | null;
  /** "deluxe edition" / "Japanese reissue" / etc. */
  disambiguation: string | null;
  /** "Official" / "Promotion" / "Bootleg" / "Pseudo-Release". */
  status: string | null;
  packaging: string | null;
  label: string | null;
  catalog_number: string | null;
}

export interface Track {
  id: number;
  mbid: string | null;
  album_id: number;
  track_number: number;
  title: string;
  duration_ms: number | null;
  has_file: boolean;
  path: string | null;
  /** True when an `.lrc` sidecar is present at `lyrics_path` (issue #16). */
  has_lyrics?: boolean;
  /** Absolute path to the `.lrc` sidecar; null when has_lyrics is false. */
  lyrics_path?: string | null;
}

export interface ArtistWithAlbums {
  artist: Artist;
  albums: Album[];
}

export interface AlbumWithTracks {
  album: Album;
  tracks: Track[];
}

export interface AddArtistRequest {
  mbid: string;
  monitored?: boolean;
}

export interface UpdateArtistRequest {
  monitored?: boolean;
  path?: string | null;
}

export interface UpdateAlbumRequest {
  monitored?: boolean;
  /**
   * Issue #7: per-album preferred release MBID. Pass an MBID returned
   * from `GET /api/v1/albums/:id/releases` to pin the album to that
   * specific edition; pass `""` to clear; omit to leave unchanged.
   */
  preferred_release_mbid?: string | null;
}

/**
 * Canonical 3-state series status. Persisted in the `series.status` SQL
 * column as one of `"Continuing" | "Ended" | "Upcoming"`. The Rust side
 * normalises TMDB's free-form `status` ({@link
 * https://developer.themoviedb.org/reference/tv-series-details "Returning
 * Series" / "In Production" / "Ended" / "Canceled" / "Pilot" / "Planned"})
 * into this closed set at lookup + refresh time. Issue #62.
 *
 * Older rows imported before #62 may still hold the raw TMDB string
 * — {@link normalizeSeriesStatus} maps both old and new values to the
 * canonical bucket so all UI surfaces can branch on the closed set.
 */
export type SeriesStatus = "Continuing" | "Ended" | "Upcoming";

export interface Series {
  id: number;
  tmdb_id: number | null;
  imdb_id: string | null;
  title: string;
  sort_title: string;
  overview: string | null;
  poster_url: string | null;
  first_air_date: string | null;
  /**
   * Series status, as persisted. Should be one of {@link SeriesStatus}
   * for newly-added or recently-refreshed series, but may be a raw
   * TMDB string for legacy rows. Use {@link normalizeSeriesStatus} to
   * collapse to the canonical bucket before branching.
   */
  status: string | null;
  monitored: boolean;
  path: string | null;
  added_by: number | null;
  created_at: string;
  updated_at: string;
  /**
   * ISO-8601 timestamp the per-episode TMDB refresh pipeline last
   * walked this series's seasons (issue #68). Distinct from
   * {@link Series.updated_at}, which tracks the series-row refresh
   * (#69). NULL when the per-episode walk hasn't visited yet.
   */
  last_episode_refresh_at: string | null;
  /**
   * Per-series naming/matching strategy (issue #65). Foundational
   * switch for the not-yet-shipped Daily (#63) and Anime (#59) parser
   * branches. Auto-detected from TMDB at add time; user can override
   * via the Add Series modal or the chip dropdown on Series detail.
   * Periodic TMDB refresh deliberately leaves this column alone —
   * see {@link SeriesType} for the enum and the Rust-side
   * `parse_series_type` for the lenient unknown → standard fallback.
   */
  series_type: SeriesType;
}

/**
 * Per-series naming/matching strategy. Mirrors `arr_tv::SeriesType`.
 * Wire format is kebab-case to match `#[serde(rename_all =
 * "kebab-case")]` on the Rust enum.
 *
 * - `standard`: the default. SxxEyy parser, western shows.
 * - `daily`: date-based matching for talk shows / news / soaps.
 * - `anime`: absolute-numbered releases (`[Group] Show - 045`).
 *
 * Today this is a foundational ship for #65 — actual parser branches
 * land in the corresponding follow-up issues.
 */
export type SeriesType = "standard" | "daily" | "anime";

/**
 * Collapse a raw / legacy series status to the canonical {@link
 * SeriesStatus} bucket. Mirrors `arr_tv::SeriesStatus::from_tmdb` on
 * the Rust side: case-insensitive, NULL → `Upcoming`, unrecognised →
 * `Upcoming` (conservative).
 */
export function normalizeSeriesStatus(raw: string | null | undefined): SeriesStatus {
  if (!raw) return "Upcoming";
  const s = raw.trim().toLowerCase();
  if (s === "") return "Upcoming";
  if (s === "returning series" || s === "in production" || s === "continuing") {
    return "Continuing";
  }
  if (s === "ended" || s === "canceled" || s === "cancelled") {
    return "Ended";
  }
  return "Upcoming";
}

export interface Season {
  id: number;
  series_id: number;
  season_number: number;
  monitored: boolean;
}

export interface Episode {
  id: number;
  series_id: number;
  season_id: number;
  season_number: number;
  episode_number: number;
  title: string | null;
  overview: string | null;
  air_date: string | null;
  runtime_minutes: number | null;
  monitored: boolean;
  has_file: boolean;
  path: string | null;
  /**
   * Issue #59: absolute episode number for anime. Computed locally
   * (TMDB doesn't expose this) by walking the series's episodes in
   * (season_number, episode_number) order, excluding S00 specials,
   * and assigning 1..N. Populated only when the parent series has
   * `series_type = 'anime'`; null otherwise. Drives anime-style
   * release matching and the optional dual-numbering filename render
   * gated by the `tv.anime_dual_numbering` setting.
   */
  absolute_number: number | null;
}

export interface SeriesWithSeasons extends Series {
  seasons: Season[];
  /**
   * Issue #66: resolved per-series "monitor specials" toggle. The
   * server resolves the per-series override (when set) against the
   * global default and surfaces the answer here so the detail page
   * can render the toggle without a settings round-trip.
   */
  monitor_specials: boolean;
}

/**
 * Series-list payload row. Mirrors {@link Series} (flattened) with the
 * extra derived flags the list page filters on.
 *
 * `specials_only` (issue #66) is `true` when the series has zero real
 * seasons (everything is in `season_number = 0`). Drives the
 * "Hide specials-only series" filter chip on `/tv`.
 */
export interface SeriesListItem extends Series {
  specials_only: boolean;
}

/**
 * Monitor preset selected when adding a series. Mirrors
 * `arr_tv::routes::MonitorPreset`. Wire format is kebab-case to match
 * `#[serde(rename_all = "kebab-case")]` on the Rust enum.
 *
 * - `all`: every episode monitored (legacy behaviour for big back-catalogs).
 * - `future`: only episodes with air_date >= today.
 * - `missing`: every episode that doesn't have a file yet.
 * - `existing`: only episodes that already have a file (un-monitor missing).
 * - `first-season`: S01 only.
 * - `last-season`: most recent season only.
 * - `none`: nothing monitored — series is added but quiet.
 */
export type MonitorPreset =
  | "all"
  | "future"
  | "missing"
  | "existing"
  | "first-season"
  | "last-season"
  | "none";

export interface AddSeriesRequest {
  tmdb_id: number;
  /** Legacy boolean — kept for back-compat with older agents. */
  monitored?: boolean;
  /** New preset. Wins over `monitored` when both are set. */
  monitor?: MonitorPreset;
  /**
   * Issue #65: per-series naming/matching strategy. When set, this
   * wins over the server-side auto-detection from TMDB metadata.
   */
  series_type?: SeriesType;
}

/** TMDB search result row — what `GET /api/v1/series/lookup` returns. */
export interface SeriesLookupResult {
  tmdb_id: number | null;
  title: string;
  year: number | null;
  overview: string | null;
  poster_url: string | null;
  /**
   * Best-guess series type to pre-populate the Add Series modal.
   * The TMDB search endpoint doesn't expose genre / origin / runtime,
   * so on the search path the server always suggests `standard` —
   * the real auto-detection runs server-side at add-time against the
   * full `/tv/{id}` payload (and the user can still override before
   * submitting). Issue #65.
   */
  suggested_type: SeriesType;
}

export interface UpdateSeriesRequest {
  monitored?: boolean;
  path?: string | null;
  /**
   * Issue #65: change a series's naming/matching strategy. PATCH
   * persists this to `series.series_type` and leaves other columns
   * alone.
   */
  series_type?: SeriesType;
  /**
   * Issue #66: per-series specials toggle. `true` opts the series in
   * for S00 episodes; `false` explicitly opts out (overrides a `true`
   * global default). Omitted = leave the override unchanged.
   */
  monitor_specials?: boolean;
}

/**
 * Body for `POST /api/v1/tv/series-type/bulk`. Sets one
 * {@link SeriesType} on many series at once. Drives the bulk admin
 * tool ("mark all of these Anime") on `/tv`.
 */
export interface BulkSetSeriesTypeRequest {
  series_ids: number[];
  series_type: SeriesType;
}

export interface BulkSetSeriesTypeResponse {
  /** Number of `series` rows updated. May be < `series_ids.length` if
   *  some IDs don't exist. */
  updated: number;
}

export interface UpdateEpisodeRequest {
  monitored?: boolean;
}

export interface UpdateSeasonRequest {
  monitored: boolean;
}

export type MediaKind = "movie" | "episode" | "album";
export type GrabStatus =
  | "queued"
  | "downloading"
  | "completed"
  | "failed"
  | "imported"
  | "removed";

export interface GrabRecord {
  id: number;
  media_kind: MediaKind;
  media_id: number;
  indexer_id: number | null;
  indexer_name: string | null;
  download_client_id: number | null;
  download_client_name: string | null;
  external_id: string;
  release_title: string;
  release_guid: string;
  size: number;
  protocol: ProtocolKind;
  status: GrabStatus;
  grabbed_at: string;
  grabbed_by: number | null;
  last_seen_at: string | null;
  imported_at: string | null;
}
